/**
 * Firestore Questions Loader
 * Helper functions to load questionnaire questions from Firestore
 * instead of static TypeScript imports
 */

import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db, auth } from './firebase';
import { adaptQuestionsUnits } from './question-unit-adapter';
import { getUserProfile, type UnitSystem } from './admin';

/**
 * Paid question packs live in their own collection.
 *
 * `questions` is world-readable because anonymous freemium users need it, so
 * anything stored there is public. Pack questions therefore live in
 * `premiumQuestions`, whose rules require the reader to own the pack.
 *
 * Firestore rules do not filter query results — they reject any query that
 * could return a forbidden document. So this collection must always be queried
 * with `where('question_set', 'in', <packs the user owns>)`; an unconstrained
 * read of it will be denied.
 */
const PREMIUM_COLLECTION = 'premiumQuestions';

/** Firestore caps `in` filters at 30 values. */
const IN_QUERY_LIMIT = 30;

export interface FirestoreQuestion {
  question_id: string;
  source_type: 'individual' | 'joint';
  text: string;
  input_type: string;
  category: string;
  subcategory?: string;
  options?: string[];
  required: boolean;
  comfort_level?: number;
  clinical_relevance?: string;
  requires_legal_review: boolean;
  person_labels?: { first: string; second: string; additional: string };
  fixed_count: boolean;
  sort_order: number;
  tags?: {
    life_phases?: string[];
    core_topics?: string[];
  };
  question_set?: string;  // e.g., 'pre-marriage' for premium sets
  /**
   * Marks this as a prediction item: "what do you think your partner answered?".
   * Holds the question_id of the self-report question being guessed at.
   * The guess is scored against the PARTNER's answer to that target, not the
   * user's own — see formatComparisonResult in comparison.ts.
   */
  predicts_question_id?: string;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface Question {
  id: string;
  sourceType: 'individual' | 'joint';
  text: string;
  type: string;
  category: string;
  subcategory?: string;
  options?: string[];
  required?: boolean;
  comfortLevel?: number;
  clinicalRelevance?: string;
  requiresLegalReview?: boolean;
  personLabels?: { first: string; second: string; additional: string };
  fixedCount?: boolean;
  tags?: {
    life_phases?: string[];
    core_topics?: string[];
    /** Legacy/alternate camelCase forms tolerated by readers */
    lifePhases?: string[];
    coreTopics?: string[];
  };
  // Primary topic assignment (set during random selection to avoid multi-topic duplication)
  primaryTopic?: string;
  // Premium question set identifier
  questionSet?: string;
  // Prediction item: id of the self-report question this one guesses at
  predictsQuestionId?: string;
}

/**
 * Convert Firestore question document to app Question format
 */
function convertFirestoreQuestion(firestoreQ: FirestoreQuestion): Question {
  return {
    id: firestoreQ.question_id,
    sourceType: firestoreQ.source_type,
    text: firestoreQ.text,
    type: firestoreQ.input_type,
    category: firestoreQ.category,
    subcategory: firestoreQ.subcategory,
    options: firestoreQ.options,
    required: firestoreQ.required,
    comfortLevel: firestoreQ.comfort_level,
    clinicalRelevance: firestoreQ.clinical_relevance,
    requiresLegalReview: firestoreQ.requires_legal_review,
    personLabels: firestoreQ.person_labels,
    fixedCount: firestoreQ.fixed_count,
    tags: firestoreQ.tags,
    questionSet: firestoreQ.question_set,
    predictsQuestionId: firestoreQ.predicts_question_id,
  };
}

/**
 * Load all questions from Firestore
 * @param useCache - If true, uses cached results (default: true)
 * @returns Array of questions in app format
 */
let cachedQuestions: Question[] | null = null;
/** Which user the cache was built for — premium content differs per user. */
let cachedForUserId: string | null = null;

/** Free questions. Public collection, readable by anonymous users. */
async function fetchFreeQuestions(): Promise<Array<{ question: Question; sortOrder: number }>> {
  const snapshot = await getDocs(collection(db, 'questions'));
  return snapshot.docs.map((doc) => {
    const data = doc.data() as FirestoreQuestion;
    return { question: convertFirestoreQuestion(data), sortOrder: data.sort_order };
  });
}

/**
 * Question packs the signed-in user has actually bought.
 *
 * Returns [] for anonymous users without touching the network. A failure here
 * is logged and swallowed: a user should still get their free questions if the
 * premium read is denied or their profile is unavailable.
 */
async function fetchPurchasedQuestions(): Promise<Array<{ question: Question; sortOrder: number }>> {
  const userId = auth.currentUser?.uid;
  if (!userId) return [];

  try {
    const profile = await getUserProfile(userId);
    const purchasedSets = profile?.purchasedSets ?? [];
    if (purchasedSets.length === 0) return [];

    const chunks: string[][] = [];
    for (let i = 0; i < purchasedSets.length; i += IN_QUERY_LIMIT) {
      chunks.push(purchasedSets.slice(i, i + IN_QUERY_LIMIT));
    }

    const snapshots = await Promise.all(
      chunks.map((chunk) =>
        getDocs(query(collection(db, PREMIUM_COLLECTION), where('question_set', 'in', chunk)))
      )
    );

    const results = snapshots.flatMap((snapshot) =>
      snapshot.docs.map((doc) => {
        const data = doc.data() as FirestoreQuestion;
        return { question: convertFirestoreQuestion(data), sortOrder: data.sort_order };
      })
    );

    console.log(
      `[loadAllQuestions] Loaded ${results.length} questions from ${purchasedSets.length} purchased set(s)`
    );
    return results;
  } catch (error) {
    console.error('[loadAllQuestions] Could not load purchased question sets:', error);
    return [];
  }
}

export async function loadAllQuestions(useCache: boolean = true): Promise<Question[]> {
  const currentUserId = auth.currentUser?.uid ?? null;

  // Cache is per-user: signing in or out changes which packs are visible.
  if (useCache && cachedQuestions && cachedForUserId === currentUserId) {
    console.log(`[loadAllQuestions] Returning ${cachedQuestions.length} cached questions`);
    return cachedQuestions;
  }

  try {
    console.log('[loadAllQuestions] Loading questions from Firestore...');

    const [free, purchased] = await Promise.all([
      fetchFreeQuestions(),
      fetchPurchasedQuestions(),
    ]);

    // De-duplicate by question id, preferring the premiumQuestions copy.
    //
    // The two collections are disjoint once migrate-premium-questions.ts has
    // run, but a question exists in both between that script's copy and delete
    // steps — and indefinitely if it is interrupted in between. Without this,
    // a buyer would see the affected questions listed twice.
    const byId = new Map<string, { question: Question; sortOrder: number }>();
    for (const entry of free) byId.set(entry.question.id, entry);
    for (const entry of purchased) byId.set(entry.question.id, entry);
    const questionsWithOrder = [...byId.values()];

    const duplicates = free.length + purchased.length - questionsWithOrder.length;
    console.log(
      `[loadAllQuestions] Fetched ${free.length} free + ${purchased.length} purchased questions` +
      (duplicates > 0 ? ` (${duplicates} duplicate id(s) collapsed — migration may be mid-flight)` : '')
    );

    // Sort by sort_order in memory (avoids need for Firestore index)
    questionsWithOrder.sort((a, b) => a.sortOrder - b.sortOrder);
    const questions = questionsWithOrder.map(item => item.question);

    // Cache the results
    cachedQuestions = questions;
    cachedForUserId = currentUserId;

    // Build the question tag cache for quick lookups
    const { buildQuestionTagCache } = await import('./question-tags');
    buildQuestionTagCache().catch(err => {
      console.error('[loadAllQuestions] Error building tag cache:', err);
    });

    console.log(`[loadAllQuestions] Successfully loaded and cached ${questions.length} questions`);
    return questions;
  } catch (error) {
    console.error('[loadAllQuestions] Error loading questions from Firestore:', error);
    throw error;
  }
}

/**
 * Load all individual questions from Firestore
 * @param useCache - If true, uses cached results (default: true)
 */
export async function loadIndividualQuestions(useCache: boolean = true): Promise<Question[]> {
  console.log('[loadIndividualQuestions] Starting...');
  const allQuestions = await loadAllQuestions(useCache);
  // Filter by the authoritative source_type, not the 'j-' id prefix.
  const individualQuestions = allQuestions.filter(q => q.sourceType === 'individual');
  console.log(`[loadIndividualQuestions] Filtered to ${individualQuestions.length} individual questions`);
  return individualQuestions;
}

/**
 * Load all joint questions from Firestore
 * @param useCache - If true, uses cached results (default: true)
 */
export async function loadJointQuestions(useCache: boolean = true): Promise<Question[]> {
  console.log('[loadJointQuestions] Starting...');
  const allQuestions = await loadAllQuestions(useCache);
  // Filter by the authoritative source_type, not the 'j-' id prefix.
  const jointQuestions = allQuestions.filter(q => q.sourceType === 'joint');
  console.log(`[loadJointQuestions] Filtered to ${jointQuestions.length} joint questions`);
  return jointQuestions;
}

/**
 * Load questions by comfort level
 * @param comfortLevel - Comfort level (1-3)
 * @param useCache - If true, uses cached results (default: true)
 */
export async function loadQuestionsByComfortLevel(
  comfortLevel: number,
  useCache: boolean = true
): Promise<Question[]> {
  const allQuestions = await loadAllQuestions(useCache);
  return allQuestions.filter(q => q.comfortLevel === comfortLevel);
}

/**
 * Load questions by category
 * @param category - Category name
 * @param useCache - If true, uses cached results (default: true)
 */
export async function loadQuestionsByCategory(
  category: string,
  useCache: boolean = true
): Promise<Question[]> {
  const allQuestions = await loadAllQuestions(useCache);
  return allQuestions.filter(q => q.category === category);
}

/**
 * Clear the questions cache
 * Call this when you want to force a fresh load from Firestore
 */
export function clearQuestionsCache() {
  cachedQuestions = null;
  cachedForUserId = null;
}

/**
 * Preload questions into cache
 * Call this on app initialization for better performance
 */
export async function preloadQuestions() {
  await loadAllQuestions(false); // Force fresh load
}

/**
 * Get unique categories from individual questions
 * Returns categories in the order they appear in the questions
 */
export async function getIndividualQuestionCategories(useCache: boolean = true): Promise<string[]> {
  console.log('[getIndividualQuestionCategories] Starting...');
  const questions = await loadIndividualQuestions(useCache);
  const categories: string[] = [];
  const seen = new Set<string>();

  questions.forEach(q => {
    if (!seen.has(q.category)) {
      categories.push(q.category);
      seen.add(q.category);
    }
  });

  console.log(`[getIndividualQuestionCategories] Found ${categories.length} categories:`, categories);
  return categories;
}

/**
 * Get unique categories from joint questions
 * Returns categories in the order they appear in the questions
 */
export async function getJointQuestionCategories(useCache: boolean = true): Promise<string[]> {
  console.log('[getJointQuestionCategories] Starting...');
  const questions = await loadJointQuestions(useCache);
  const categories: string[] = [];
  const seen = new Set<string>();

  questions.forEach(q => {
    if (!seen.has(q.category)) {
      categories.push(q.category);
      seen.add(q.category);
    }
  });

  console.log(`[getJointQuestionCategories] Found ${categories.length} categories:`, categories);
  return categories;
}

/**
 * Load individual questions adapted for user's unit system preference
 * @param unitSystem - User's preferred unit system ('imperial' or 'metric')
 * @param useCache - If true, uses cached results (default: true)
 * @returns Array of questions adapted to the user's unit system
 */
export async function loadIndividualQuestionsWithUnits(
  unitSystem: UnitSystem,
  useCache: boolean = true
): Promise<Question[]> {
  const questions = await loadIndividualQuestions(useCache);
  return adaptQuestionsUnits(questions, unitSystem);
}

/**
 * Load joint questions adapted for user's unit system preference
 * @param unitSystem - User's preferred unit system ('imperial' or 'metric')
 * @param useCache - If true, uses cached results (default: true)
 * @returns Array of questions adapted to the user's unit system
 */
export async function loadJointQuestionsWithUnits(
  unitSystem: UnitSystem,
  useCache: boolean = true
): Promise<Question[]> {
  const questions = await loadJointQuestions(useCache);
  return adaptQuestionsUnits(questions, unitSystem);
}

/**
 * Load all questions adapted for user's unit system preference
 * @param unitSystem - User's preferred unit system ('imperial' or 'metric')
 * @param useCache - If true, uses cached results (default: true)
 * @returns Array of all questions adapted to the user's unit system
 */
export async function loadAllQuestionsWithUnits(
  unitSystem: UnitSystem,
  useCache: boolean = true
): Promise<Question[]> {
  const questions = await loadAllQuestions(useCache);
  return adaptQuestionsUnits(questions, unitSystem);
}
